import { ErrorInfo, ReactNode, Component } from 'react';

import { Logger } from '@vk-hr-tek/core/logger';
import { UpdatorService } from '@vk-hr-tek/core/updator';

import { PageError } from '../page';

interface ErrorBoundaryProps {
  children: ReactNode | ReactNode[];
  logger: Logger;
  updator: UpdatorService;
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
}

const isChunkLoadError = (error: Error): boolean => {
  const message = error?.message || '';

  return (
    error?.name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk')
  );
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(
    error: Error & { cause?: Error },
    { componentStack }: ErrorInfo,
  ) {
    if (isChunkLoadError(error)) {
      this.props.logger.warning(error, {
        tags: {
          vkdoc_error_type: 'chunk_load',
        },
        context: {
          react: {
            stack: componentStack,
          },
        },
      });
      this.props.updator.onChunkError();
      return;
    }

    this.props.logger.fatal(error, {
      tags: {
        vkdoc_error_type: 'unknown',
      },
      context: {
        react: {
          stack: componentStack,
        },
      },
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return null;
      }

      return <PageError status={500} />;
    }

    return this.props.children;
  }
}
